import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button } from '@airia/ui';
import { RefreshCw } from 'lucide-react';
import { stripHtml } from '../../lib/stripHtml';
import { formatEmailBody } from '../../lib/formatEmailBody';
import { isComposeMode } from '../../types/office';
import { logger } from '../../lib/logger';
const ReplaceEmailButton = ({ replaceEmailData }) => {
    const [isReplacing, setIsReplacing] = useState(false);
    const [error, setError] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const plainTextBody = stripHtml(replaceEmailData.body);
    const shouldShowToggle = plainTextBody.length > 100;
    // Debug log to see what data we received
    logger.log('ReplaceEmailButton rendered with data:', {
        hasSubject: !!replaceEmailData.subject,
        subject: replaceEmailData.subject,
        bodyLength: replaceEmailData.body.length,
    });
    const handleReplaceEmail = async () => {
        try {
            setIsReplacing(true);
            setError(null);
            if (typeof Office !== 'undefined' &&
                Office.context.mailbox &&
                Office.context.mailbox.item) {
                const item = Office.context.mailbox.item;
                // Check if we're in compose mode using type guard
                if (!isComposeMode(item)) {
                    setError('This action is only available in compose mode');
                    setIsReplacing(false);
                    return;
                }
                // TypeScript now knows item is OfficeComposeItem
                let subjectUpdateFailed = false;
                let bodyUpdateFailed = false;
                // Update subject if provided
                if (replaceEmailData.subject && item.subject) {
                    const subjectToSet = replaceEmailData.subject;
                    logger.log('Attempting to set subject:', subjectToSet);
                    await new Promise((resolve) => {
                        item.subject.setAsync(subjectToSet, (asyncResult) => {
                            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                logger.error('Error replacing email subject:', asyncResult.error);
                                subjectUpdateFailed = true;
                            }
                            else {
                                logger.log('Subject set successfully:', subjectToSet);
                            }
                            resolve();
                        });
                    });
                }
                else {
                    logger.log('Subject update skipped. Has subject?', !!replaceEmailData.subject, 'Has item.subject?', !!item.subject);
                }
                // Replace the email body content inline
                if (item.body) {
                    const bodyToSet = formatEmailBody(replaceEmailData.body);
                    await new Promise((resolve) => {
                        item.body.setAsync(bodyToSet, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
                            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                logger.error('Error replacing email body:', asyncResult.error);
                                bodyUpdateFailed = true;
                            }
                            resolve();
                        });
                    });
                    // Update UI state and errors after both operations complete
                    setIsReplacing(false);
                    if (subjectUpdateFailed && bodyUpdateFailed) {
                        setError('Failed to update both subject and content');
                    }
                    else if (subjectUpdateFailed) {
                        setError('Content updated, but subject update failed');
                    }
                    else if (bodyUpdateFailed) {
                        setError('Failed to replace email content');
                    }
                }
                else {
                    logger.error('body not available');
                    setError('Cannot replace email content in this context');
                    setIsReplacing(false);
                }
            }
            else {
                logger.error('Office context or mailbox item not available');
                setError('Office context not available');
                setIsReplacing(false);
            }
        }
        catch (error) {
            logger.error('Error replacing email content:', error);
            setError('An unexpected error occurred');
            setIsReplacing(false);
        }
    };
    return (_jsxs("div", { className: "my-2 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg", children: [_jsxs("div", { className: "flex items-start gap-3", children: [_jsx("div", { className: "p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-full", children: _jsx(RefreshCw, { className: "size-4 text-emerald-600 dark:text-emerald-400" }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "text-sm font-medium text-foreground mb-1", children: "Replace Email Content" }), _jsxs("div", { className: "text-xs text-muted-foreground space-y-1", children: [replaceEmailData.subject && (_jsxs("div", { children: [_jsx("span", { className: "font-medium", children: "New Subject:" }), ' ', replaceEmailData.subject] })), _jsxs("div", { className: isExpanded ? '' : 'max-h-20 overflow-y-auto', children: [_jsx("span", { className: "font-medium", children: "New Body:" }), ' ', isExpanded ? plainTextBody : plainTextBody.substring(0, 100), !isExpanded && shouldShowToggle && '...'] }), shouldShowToggle && (_jsx("button", { onClick: () => setIsExpanded(!isExpanded), className: "text-emerald-600 dark:text-emerald-400 hover:underline text-xs mt-1", children: isExpanded ? 'Show Less' : 'Show More' }))] }), error && (_jsx("div", { className: "text-xs text-red-600 dark:text-red-400 mt-1", children: error }))] })] }), _jsxs(Button, { onClick: handleReplaceEmail, size: "sm", className: "w-full mt-3 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500", disabled: isReplacing, children: [_jsx(RefreshCw, { className: `size-4 mr-2 ${isReplacing ? 'animate-spin' : ''}` }), isReplacing ? 'Replacing...' : 'Replace Email Content'] })] }));
};
export default ReplaceEmailButton;
